import { useEffect, useRef } from "react";

interface Args {
  keys?: Record<string, () => void>;
  konami?: () => void;
  whoami?: () => void;
}

const KONAMI_SEQ = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "b",
  "a",
] as const;

const WHOAMI = "whoami";

const isTypingTarget = (t: EventTarget | null): boolean => {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || t.isContentEditable;
};

export const useKeyboardCapture = ({ keys, konami, whoami }: Args): void => {
  const konamiBuf = useRef<string[]>([]);
  const whoamiBuf = useRef<string>("");

  useEffect(() => {
    konamiBuf.current = [];
    whoamiBuf.current = "";
    const onKeydown = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;

      if (konami) {
        konamiBuf.current = [...konamiBuf.current, e.key].slice(
          -KONAMI_SEQ.length,
        );
        const matched = konamiBuf.current.join("|") === KONAMI_SEQ.join("|");
        if (matched) {
          konami();
          konamiBuf.current = [];
        }
      }

      if (whoami) {
        whoamiBuf.current = (whoamiBuf.current + e.key).slice(-WHOAMI.length);
        if (whoamiBuf.current === WHOAMI) {
          whoami();
          whoamiBuf.current = "";
        }
      }

      if (keys) {
        const handler = keys[e.key];
        if (handler) handler();
      }
    };

    window.addEventListener("keydown", onKeydown);
    return () => window.removeEventListener("keydown", onKeydown);
  }, [keys, konami, whoami]);
};
